/**
 * useCopyToClipboard Hook
 *
 * Reusable hook for copying text to clipboard with success state management
 *
 * @param {number} resetTimeout - Time in ms before resetting isCopied state (default: 2000)
 * @returns {Object} { isCopied, copy } - Success state and copy function
 *
 * @example
 * const { isCopied, copy } = useCopyToClipboard();
 *
 * // In your component
 * <button onClick={() => copy('Hello World')}>
 *   {isCopied ? '✓ Copied!' : 'Copy'}
 * </button>
 */

import { useState, useCallback, useRef, useEffect } from 'react';

const useCopyToClipboard = (resetTimeout = 2000) => {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);

      // Clear existing timeout if any
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout to reset isCopied state
      timeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, resetTimeout);
    } catch (err) {
      console.error('Failed to copy text:', err);
      setIsCopied(false);
    }
  }, [resetTimeout]);

  return { isCopied, copy };
};

export default useCopyToClipboard;
